// Pantalla "Mis tareas" del técnico — vista móvil "lo que me toca", agrupada
// por vencimiento, funcionando sin señal (useMisTareas es read-through sobre
// el store offline). Encabezado simple (no PageHeader pesado), siguiendo el
// estilo de SpecialistOrders.jsx que es la superficie del técnico.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Space,
  Spin,
  Empty,
  Alert,
  Collapse,
  Tag,
  Button,
  Segmented,
} from 'antd';
import { DisconnectOutlined, ReloadOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import dayjs from 'libs/dayjs';
import useAuth from 'hooks/useAuth';
import { useMisTareas } from 'hooks/useMisTareas';
import {
  agruparPorVencimiento,
  esMiTarea,
  fechaReferencia,
  hoyEnSantiago,
} from 'services/misTareas';

const { Title, Text } = Typography;

const SECCIONES = [
  { key: 'vencidas', titulo: 'Vencidas', tono: 'error' },
  { key: 'estaSemana', titulo: 'Esta semana', tono: 'warning' },
  { key: 'proximas', titulo: 'Próximas', tono: 'neutral' },
];

const PLEGADAS = [
  { key: 'sinFecha', titulo: 'Sin fecha' },
  { key: 'terminadas', titulo: 'Terminadas' },
];

function TareaCard({ tarea, colorBorde, navigate, mostrarResponsable }) {
  const ref = fechaReferencia(tarea);
  return (
    <Card
      size="small"
      hoverable
      className="mis-tareas-card"
      style={{ borderColor: colorBorde }}
      onClick={() => navigate(`/specialist/order/${tarea.id_order}`)}
    >
      <div style={{ fontWeight: 600 }}>{tarea.description}</div>
      {tarea.order_address ? (
        <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
          {tarea.order_address}
        </Text>
      ) : null}
      {mostrarResponsable ? (
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          {tarea.assigned_to_nombre || 'Sin responsable'}
        </Text>
      ) : null}
      <Text type="secondary" style={{ fontSize: 12 }}>
        {ref ? dayjs(ref).format('DD/MM') : 'Sin fecha'}
      </Text>
    </Card>
  );
}

function Seccion({ titulo, tono, tareas, token, navigate, mostrarResponsable }) {
  if (!tareas || tareas.length === 0) return null;

  const colorBorde =
    tono === 'error' ? token.colorError : tono === 'warning' ? token.colorWarning : undefined;

  return (
    <div style={{ marginBottom: 16 }}>
      <Space align="center" style={{ marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0, color: colorBorde }}>
          {titulo}
        </Title>
        <Tag>{tareas.length}</Tag>
      </Space>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {tareas.map((tarea) => (
          <TareaCard
            key={tarea.id}
            tarea={tarea}
            colorBorde={colorBorde}
            navigate={navigate}
            mostrarResponsable={mostrarResponsable}
          />
        ))}
      </Space>
    </div>
  );
}

const FILTRO_OPCIONES = [
  { label: 'Mías', value: 'mias' },
  { label: 'Todas', value: 'todas' },
];

export default function MisTareasPage() {
  const navigate = useNavigate();
  const { tareas, loading, offline, recargar } = useMisTareas();
  const { user } = useAuth();
  const { token } = theme.useToken();
  const [cargado, setCargado] = useState(false);
  const [filtro, setFiltro] = useState('mias');

  React.useEffect(() => {
    if (!cargado) {
      recargar();
      setCargado(true);
    }
  }, [cargado, recargar]);

  // Filtro 100% en memoria sobre la lista ya cargada por useMisTareas (que a
  // su vez es read-through sobre el caché offline) — cambiar de filtro nunca
  // dispara una llamada a la API, así que funciona sin conexión.
  const miUserId = user?.id;
  const tareasMias = useMemo(
    () => tareas.filter((t) => esMiTarea(t, miUserId)),
    [tareas, miUserId]
  );
  const tareasFiltradas = filtro === 'todas' ? tareas : tareasMias;

  const grupos = useMemo(
    () => agruparPorVencimiento(tareasFiltradas, hoyEnSantiago()),
    [tareasFiltradas]
  );

  // `cargado` se vuelve true recién cuando el efecto de montaje dispara la
  // primera carga (ver useEffect abajo). Lo usamos como guarda para no
  // mostrar "No hay tareas pendientes" en el frame inicial, antes de que se
  // haya intentado siquiera la primera carga (evita el flash del vacío).
  const sinTareas =
    cargado &&
    !loading &&
    grupos.vencidas.length === 0 &&
    grupos.estaSemana.length === 0 &&
    grupos.proximas.length === 0 &&
    grupos.sinFecha.length === 0 &&
    grupos.terminadas.length === 0;

  // Con el filtro "Mías" vacío pero habiendo tareas ajenas cargadas, el
  // vacío mudo es una mala experiencia — sugerimos mirar "Todas".
  const hayTareasAjenas = filtro === 'mias' && tareas.length > tareasMias.length;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 12 }}>
      <Space
        align="center"
        style={{ justifyContent: 'space-between', width: '100%', marginBottom: 12 }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Mis tareas
          </Title>
          <Text type="secondary">Tareas asignadas, agrupadas por fecha de vencimiento</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={recargar} loading={loading} />
      </Space>

      <Segmented
        block
        options={FILTRO_OPCIONES}
        value={filtro}
        onChange={setFiltro}
        style={{ marginBottom: 12 }}
      />

      {offline && (
        <Alert
          type="warning"
          showIcon
          icon={<DisconnectOutlined />}
          message="Sin conexión — mostrando lo guardado"
          style={{ marginBottom: 12 }}
        />
      )}

      {!cargado || (loading && tareas.length === 0) ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : sinTareas ? (
        <Empty
          description={
            hayTareasAjenas
              ? 'No tiene tareas propias pendientes. Cambie a "Todas" para ver las del resto del equipo.'
              : 'No hay tareas pendientes'
          }
        />
      ) : (
        <>
          {SECCIONES.map((s) => (
            <Seccion
              key={s.key}
              titulo={s.titulo}
              tono={s.tono}
              tareas={grupos[s.key]}
              token={token}
              navigate={navigate}
              mostrarResponsable={filtro === 'todas'}
            />
          ))}

          {PLEGADAS.filter((p) => grupos[p.key] && grupos[p.key].length > 0).map((p) => (
            <Collapse
              key={p.key}
              ghost
              style={{ marginBottom: 8 }}
              items={[
                {
                  key: p.key,
                  label: (
                    <Space>
                      <Text>{p.titulo}</Text>
                      <Tag>{grupos[p.key].length}</Tag>
                    </Space>
                  ),
                  children: (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {grupos[p.key].map((tarea) => (
                        <TareaCard
                          key={tarea.id}
                          tarea={tarea}
                          navigate={navigate}
                          mostrarResponsable={filtro === 'todas'}
                        />
                      ))}
                    </Space>
                  ),
                },
              ]}
            />
          ))}
        </>
      )}
    </div>
  );
}
